import { z } from "zod";
import { canonicalJson } from "./canonical-json";
import {
  actionCategorySchema,
  type ConnectionScope,
  type ConnectionStatus,
  type Decision,
} from "./connections";
import { type PinSummary, pinnedTableSchema } from "./data-tables";
import { FILE_TYPES, normalizeMediaType } from "./files";
import { idSchema } from "./ids";
import {
  type AmountValue,
  amountValueSchema,
  countValueSchema,
  type DurationUnit,
  type DurationValue,
  durationValueSchema,
  memberRefSchema,
  OUTPUT_VALUE_KINDS,
  type OutputValueKind,
  SLOT_VALUE_TYPES,
  type TimeOfDayValue,
  timeOfDayValueSchema,
} from "./value-types";

export { canonicalJson };
export {
  type AmountValue,
  DURATION_UNITS,
  type DurationUnit,
  type DurationValue,
  type MemberRef,
  memberRefSchema,
  type TimeOfDayValue,
} from "./value-types";

/**
 * What a process's definition is, as drafts and releases hold it: Procedure storage V2 (decision
 * 113), on the representation of V2 and its 27 October amendment (decisions 60, 61 and 107).
 * Browser-safe: the web app and the servers read and check a definition with the same schemas.
 * Storage and the rules that change a draft live in the API's processes module.
 */

/** What a step does to the world. Structure follows from it: a step that changes or sends needs
 * its result read back and an approval bound to what it sends. */
export const EFFECT_KINDS = ["looks", "changes", "sends", "none"] as const;
export type EffectKind = (typeof EFFECT_KINDS)[number];

/** Why one step comes after another (decision 60). */
export const AFTER_REASONS = ["derived", "declared", "hold"] as const;
export type AfterReason = (typeof AFTER_REASONS)[number];

/** The answers a step may write. The rest take the process's defaults (amendment A5). */
export const ANSWER_FIELDS = [
  "doneWhen",
  "who",
  "each",
  "onlyWhen",
  "uses",
  "limits",
  "ifItCant",
] as const;
export type AnswerField = (typeof ANSWER_FIELDS)[number];

/** The kinds of output a step can record, and so of what a case can deliver (amendment A7). */
export const OUTPUT_TYPES = ["value", "file", "record", "message", "attestation"] as const;
export type OutputType = (typeof OUTPUT_TYPES)[number];

/** Whether an open question stops a release, or only holds the cases that reach it (decision 107). */
export const QUESTION_KINDS = ["blocking", "holding"] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];

/**
 * What a question is about: the whole process, one step, one row of a table, or a row a table
 * does not have yet, by its key (ENG-1075).
 */
export const QUESTION_TARGET_KINDS = ["process", "step", "table_row", "table_key"] as const;
export type QuestionTargetKind = (typeof QUESTION_TARGET_KINDS)[number];

/** Where a question was raised: while a draft was edited, or by a case that reached it. */
export const RAISED_IN_KINDS = ["draft", "case"] as const;
export type RaisedInKind = (typeof RAISED_IN_KINDS)[number];

/**
 * How an answer reaches the cases a question holds (decision 107): it needs no change (`none`),
 * it was made as a live table edit (`table`), or it changes the definition and reaches cases only
 * on the release that carries it (`release`).
 */
export const QUESTION_RESOLUTIONS = ["none", "table", "release"] as const;
export type QuestionResolutionKind = (typeof QUESTION_RESOLUTIONS)[number];

const text = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !value.includes("\0"));

const at = text(200).optional();

/**
 * Where a value came from: a workspace file (an imported SOP, a transcript) or a chat message,
 * with the place inside it ("line 13", "page 2", "04:12"); or a recording of the chat, by the tape
 * position of the person's input it came from (`seq`), so a correction or a removal of that input
 * after the recording ended (ENG-1029) can find the values that used it.
 */
export const sourceRefSchema = z.discriminatedUnion("kind", [
  /**
   * `passage` is the SOP's ¶ number as `sopPassages` splits the file (ENG-1073 item 2); absent on
   * every source written before passages, which parse and hash as they did.
   */
  z
    .object({
      kind: z.literal("file"),
      id: idSchema("file"),
      at,
      passage: z.int().positive().max(10_000).optional(),
    })
    .strict(),
  z.object({ kind: z.literal("message"), id: idSchema("message"), at }).strict(),
  z
    .object({
      kind: z.literal("recording"),
      id: idSchema("recording"),
      seq: z.int().positive(),
      at,
    })
    .strict(),
]);
export type SourceRef = z.infer<typeof sourceRefSchema>;

/**
 * A value the firm decides, with where it came from and who decided it. `decidedBy` is null while
 * no person has decided it: the system wrote it into a draft. The service sets it from a change's
 * author and never takes it from a client: a person writing a value has decided it, and whoever
 * releases a draft decides every value of it still null (decision
 * `2026-10-31-steps-view-decided-as-a-whole`).
 */
export const decided = <Value extends z.ZodType>(value: Value) =>
  z
    .object({
      value,
      sources: z.array(sourceRefSchema).max(20),
      decidedBy: idSchema("user").nullable(),
    })
    .strict();
export type Decided<Value> = {
  value: Value;
  sources: SourceRef[];
  decidedBy: string | null;
};

// Typed slots (plan `docs/plans/2026-11-01-typed-slots-and-undo-stack.md`, Part 1). An exact value
// inside a step's sentence is kept beside the sentence, in the step's `slots`, and named from the
// text by a `{{key}}` token. A person changes the value without touching the wording; the wording,
// the range and which slots exist are the system's (decision 64). Which tokens and slots a step
// may hold is checked by the service on the steps a batch writes, never by these schemas, so text
// written before slots that happens to hold braces still parses and keeps its hash.

/** The kinds of exact value a slot holds: the value-type catalog's member and ranged types. */
export const SLOT_TYPES = SLOT_VALUE_TYPES;
export type SlotType = (typeof SLOT_TYPES)[number];

/** A slot key, the same rule as an output key with a length cap. */
export const SLOT_KEY = /^[a-z][A-Za-z0-9]{0,31}$/;

/** A slot's token in a step's text: `{{approvalWait}}`. Global, so reset `lastIndex` or use `matchAll`. */
export const SLOT_TOKEN = /\{\{([a-z][A-Za-z0-9]{0,31})\}\}/g;

/** A duration's length in its family's unit: minutes for calendar time, days for business days. */
const CALENDAR_MINUTES: Partial<Record<DurationUnit, number>> = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
};

/** One ranged slot value, as a comparable position, or null for no ranged type. */
type Ranged =
  | { type: "duration"; value: DurationValue }
  | { type: "count"; value: number }
  | { type: "amount"; value: AmountValue }
  | { type: "timeOfDay"; value: TimeOfDayValue };

/**
 * How two values of one ranged slot type compare: negative, zero or positive, or null when they
 * do not compare (a duration in another unit family, an amount in another currency).
 */
export function compareSlotValues<Slot extends Ranged>(
  type: Slot["type"],
  a: Slot["value"],
  b: Slot["value"],
): number | null {
  switch (type) {
    case "count":
      return (a as number) - (b as number);
    case "timeOfDay":
      return (a as TimeOfDayValue).time.localeCompare((b as TimeOfDayValue).time);
    case "amount": {
      const [x, y] = [a as AmountValue, b as AmountValue];
      return x.currency === y.currency ? x.minor - y.minor : null;
    }
    case "duration": {
      const [x, y] = [a as DurationValue, b as DurationValue];
      if ((x.unit === "businessDays") !== (y.unit === "businessDays")) return null;
      if (x.unit === "businessDays") return x.amount - y.amount;
      return (
        x.amount * (CALENDAR_MINUTES[x.unit] ?? 1) - y.amount * (CALENDAR_MINUTES[y.unit] ?? 1)
      );
    }
  }
}

/**
 * The range a person may move a value in: the value Abacor read from the SOP (`anchor`), optional
 * bounds, and which direction relaxes the step (`looser`). The bounds and the anchor compare, and
 * the range holds its anchor.
 */
const rangeSchema = <Value extends z.ZodType<Ranged["value"]>>(
  type: Ranged["type"],
  value: Value,
) =>
  z
    .object({
      min: value.optional(),
      max: value.optional(),
      anchor: value,
      looser: z.enum(["up", "down"]),
    })
    .strict()
    .refine((parsed) => {
      const range = parsed as { min?: unknown; max?: unknown; anchor: unknown };
      const compare = (a: unknown, b: unknown) => compareSlotValues(type, a as never, b as never);
      const below = range.min === undefined ? 0 : compare(range.min, range.anchor);
      const above = range.max === undefined ? 0 : compare(range.anchor, range.max);
      return below !== null && above !== null && below <= 0 && above <= 0;
    });

const slotSources = z.array(sourceRefSchema).max(20);

/** The slot shapes without who decided them: the system's whole-slot write. */
const slotBodies = {
  member: z.object({ type: z.literal("member"), value: memberRefSchema, sources: slotSources }),
  duration: z.object({
    type: z.literal("duration"),
    value: durationValueSchema,
    range: rangeSchema("duration", durationValueSchema),
    sources: slotSources,
  }),
  count: z.object({
    type: z.literal("count"),
    value: countValueSchema,
    range: rangeSchema("count", countValueSchema),
    sources: slotSources,
  }),
  amount: z.object({
    type: z.literal("amount"),
    value: amountValueSchema,
    range: rangeSchema("amount", amountValueSchema),
    sources: slotSources,
  }),
  timeOfDay: z.object({
    type: z.literal("timeOfDay"),
    value: timeOfDayValueSchema,
    range: rangeSchema("timeOfDay", timeOfDayValueSchema),
    sources: slotSources,
  }),
};

const decidedBy = idSchema("user").nullable();

/** A slot as a step holds it: typed value, its range, where it came from and who decided it. */
export const slotSchema = z.discriminatedUnion("type", [
  slotBodies.member.extend({ decidedBy }).strict(),
  slotBodies.duration.extend({ decidedBy }).strict(),
  slotBodies.count.extend({ decidedBy }).strict(),
  slotBodies.amount.extend({ decidedBy }).strict(),
  slotBodies.timeOfDay.extend({ decidedBy }).strict(),
]);
export type Slot = z.infer<typeof slotSchema>;

/** The value of a slot that has a range: a duration, a count, an amount or a time of day. */
export const rangedSlotValueSchema = z.union([
  durationValueSchema,
  countValueSchema,
  amountValueSchema,
  timeOfDayValueSchema,
]);

/** A whole slot as the system writes it; who decided it is set by the service. */
export const slotInputSchema = z.discriminatedUnion("type", [
  slotBodies.member.strict(),
  slotBodies.duration.strict(),
  slotBodies.count.strict(),
  slotBodies.amount.strict(),
  slotBodies.timeOfDay.strict(),
]);
export type SlotInput = z.infer<typeof slotInputSchema>;

/**
 * A person's in-place edit of a slot: the value only. Its type and range come from the step, so the
 * value is checked against the slot's own schema by the service.
 */
export const personSlotInputSchema = z
  .object({ value: z.unknown(), sources: slotSources.optional() })
  .strict();
export type PersonSlotInput = z.infer<typeof personSlotInputSchema>;

/**
 * A step's own people: any one of them may do or confirm it. Absent means the step asks the
 * playbook's person. 1 to 10 members, no repeats.
 */
export const whoSchema = z
  .object({
    members: z
      .array(memberRefSchema)
      .min(1)
      .max(10)
      .refine((members) => new Set(members.map((member) => member.userId)).size === members.length),
    sources: slotSources,
    decidedBy,
  })
  .strict();
export type Who = z.infer<typeof whoSchema>;

/** Who does the work: Abacor, Abacor with a person confirming, or a person. */
export type Level = "abacor" | "confirms" | "human";
/** The levels the system may set on a step; absent is derived (`levelOf`). */
export const STEP_LEVELS = ["confirms", "human"] as const;

/** An output's key: unique within its step, and how a later step or Delivers names it. */
const outputKey = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][A-Za-z0-9]*$/);
/**
 * A Starts when field's key, as declared and as a case's input names it: the same rule as an
 * output's key, so every field a playbook declares can be given.
 */
export const startFieldKeySchema = outputKey;

const afterSchema = z
  .object({
    stepId: idSchema("processStep"),
    reason: z.enum(AFTER_REASONS),
    note: text(200).optional(),
  })
  .strict();

const answer = decided(text(4000));

/**
 * Where a How note learned in a run came from: a person's correction of a case at this step, and
 * the case's subject as it read then ("PO 4471"), so the note says it without reading the case.
 */
export const learnedFromSchema = z
  .object({
    caseId: idSchema("case"),
    correctedBy: idSchema("user"),
    subject: text(200),
  })
  .strict();
export type LearnedFrom = z.infer<typeof learnedFromSchema>;

/**
 * One note of a step's How (Paper p-15-0, SV23): know-how a new hire would be told, such as which
 * field a number goes in, as a decided sentence with its sources. A note told by a recording, an
 * SOP or the chat has only its sources; one learned in a run also says whose correction it was.
 * Notes are guidance Abacor reads, never steps of a script: they are unnumbered and say nothing of
 * order, branches or limits, which stay the step's own fields.
 */
export const howNoteSchema = z
  .object({ ...decided(text(1000)).shape, learned: learnedFromSchema.optional() })
  .strict();
export type HowNote = z.infer<typeof howNoteSchema>;

/** The most notes a step's How holds. */
export const HOW_NOTES_MAX = 30;

/** How a step uses a table it reads (decision 116). */
export const READ_USES = ["each", "lookup", "onlyWhen"] as const;
export type ReadUse = (typeof READ_USES)[number];

const readSchema = z
  .object({
    tableId: idSchema("dataTable"),
    columnIds: z
      .array(idSchema("dataColumn"))
      .min(1)
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length),
    use: z.enum(READ_USES),
  })
  .strict();

// What a step uses (plan Part 4, decision
// `2026-11-02-a-step-names-its-accounts-and-sets-them-up-in-place`). The app, site or column and
// what the step does there are structure the system writes; the account is an exact value a person
// picks in place, decided on its own. Tables a step only looks things up in stay under `reads`.

/** The account a use goes through, by connection ID; null while none is set. */
const useAccount = decided(idSchema("connection").nullable());
const useKey = z.string().regex(SLOT_KEY);

export const useSchema = z.discriminatedUnion("kind", [
  /** An app Abacor connects to directly, by the connections catalog's toolkit slug. */
  z
    .object({
      key: useKey,
      kind: z.literal("app"),
      app: z.string().min(1).max(100),
      does: actionCategorySchema,
      account: useAccount,
    })
    .strict(),
  /** A website login for one site, by its host. Website logins are connections too. */
  z
    .object({
      key: useKey,
      kind: z.literal("site"),
      site: z.string().min(1).max(253),
      does: actionCategorySchema,
      account: useAccount,
    })
    .strict(),
  /** A column of a table holding one login per row: each row has its own account. */
  z
    .object({
      key: useKey,
      kind: z.literal("column"),
      tableId: idSchema("dataTable"),
      columnId: idSchema("dataColumn"),
      does: actionCategorySchema,
    })
    .strict(),
]);
export type Use = z.infer<typeof useSchema>;
/** A use that goes through one account: an app or a site. */
export type AccountUse = Extract<Use, { kind: "app" | "site" }>;

/** A use's account, as a change names it: `uses.<key>`. */
export type AccountField = `uses.${string}`;

/** What the fit rule reads of an account: its app or site, whose it is, and its state. */
export type FitAccount = {
  /** The toolkit slug of an app connection; null for a website login. */
  app: string | null;
  /** The host of a website login; null for an app connection. */
  site: string | null;
  scope: ConnectionScope;
  ownerUserId: string | null;
  status: ConnectionStatus;
};

/** Who sets an account on a step: a person, by user ID, or the system writing for a chat's owner. */
export type AccountSetter = { kind: "person" | "system"; userId: string };

/** Whether an account is of the app or site a use names. A login column names neither. */
export const isUseTarget = (use: Use, account: Pick<FitAccount, "app" | "site">): boolean =>
  use.kind === "app"
    ? account.app === use.app
    : use.kind === "site"
      ? account.site === use.site
      : false;

/**
 * Which accounts fit a use (plan Part 4, Q20), the one rule the server's checks, the chat's list
 * and the picker all read: a live account of the use's app or site that is the workspace's
 * (`workspace`) or the draft creator's own (`own`). Another member's own, a removed account, one of
 * another app and any account for a login column fit nothing (null).
 */
export function accountFit(
  use: Use,
  account: FitAccount,
  creatorUserId: string,
): "workspace" | "own" | null {
  return isUseTarget(use, account) ? whoseFit(account, creatorUserId) : null;
}

/**
 * `accountFit` for an account already known to be of the use's app or site: the workspace's, the
 * draft creator's own, or null for another member's own or a removed account.
 */
export function whoseFit(
  account: Pick<FitAccount, "scope" | "ownerUserId" | "status">,
  creatorUserId: string,
): "workspace" | "own" | null {
  if (account.status === "disconnected") return null;
  if (account.scope === "workspace") return "workspace";
  return account.ownerUserId === creatorUserId ? "own" : null;
}

/**
 * Whether `setter` may set the account on a use: any workspace account that fits, and the
 * creator's own only when the creator sets it themself. An admin changing someone else's draft,
 * and the system, set workspace accounts only.
 */
export function maySetAccount(
  use: Use,
  account: FitAccount,
  creatorUserId: string,
  setter: AccountSetter,
): boolean {
  const fit = accountFit(use, account, creatorUserId);
  return (
    fit === "workspace" ||
    (fit === "own" && setter.kind === "person" && setter.userId === creatorUserId)
  );
}

/**
 * The draft, step and use a sign-in started from, so its completion can set that use's account
 * (plan Task 16). Kept on the connect attempt.
 */
export const forStepSchema = z
  .object({
    draftId: idSchema("processDraft"),
    stepId: idSchema("processStep"),
    use: useKey,
  })
  .strict();
export type ForStep = z.infer<typeof forStepSchema>;

// Typed outputs (plan Part 3, "Produces" on board SV16). Each output may declare a shape for its
// type, and each step may name the earlier outputs it reads. Both are structure the system writes
// (decision 64), and both are absent when unset, so earlier steps and releases hash as before. An
// output with no shape is a legacy output, typed by `type` alone.

export { OUTPUT_VALUE_KINDS, type OutputValueKind };

/** The kinds of file an output may declare; `any` takes every file a chat accepts. */
export const OUTPUT_FILE_KINDS = ["pdf", "image", "spreadsheet", "document", "any"] as const;
export type OutputFileKind = (typeof OUTPUT_FILE_KINDS)[number];

/** The file kind of an accepted media type (Q15), or null for one no output kind but `any` takes. */
export function fileKindOf(mediaType: string): Exclude<OutputFileKind, "any"> | null {
  const type = normalizeMediaType(mediaType);
  if (type === null) return null;
  if (type === "application/pdf") return "pdf";
  if (FILE_TYPES[type] === "image") return "image";
  if (
    type === "text/csv" ||
    type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return "spreadsheet";
  if (type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    return "document";
  return null;
}

/** What an output is for: what the step is for, or support kept beside it (Q12). */
export const OUTPUT_PURPOSES = ["required", "evidence"] as const;
export type OutputPurpose = (typeof OUTPUT_PURPOSES)[number];

/** Whether a step's value outputs are one per case or one per row under its Each. */
export const OUTPUT_PER = ["case", "line"] as const;

const valueShapeSchema = z
  .object({
    kind: z.enum(OUTPUT_VALUE_KINDS),
    /** Only for a choice, which needs them. */
    choices: z
      .array(text(200))
      .min(1)
      .max(50)
      .refine((choices) => new Set(choices).size === choices.length)
      .optional(),
    /** Only for an amount: when set, an amount in another currency is refused. */
    currency: amountValueSchema.shape.currency.optional(),
    per: z.enum(OUTPUT_PER),
  })
  .strict()
  .refine((shape) => (shape.kind === "choice") === (shape.choices !== undefined))
  .refine((shape) => shape.currency === undefined || shape.kind === "amount");

/** Who a message goes to: a member slot of the step, an earlier value holding the address, or words. */
const recipientSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("member"), slot: z.string().regex(SLOT_KEY) }).strict(),
  z.object({ kind: z.literal("output"), stepId: idSchema("processStep"), key: outputKey }).strict(),
  z.object({ kind: z.literal("described"), text: text(500) }).strict(),
]);

const outputOf = <Type extends OutputType, Shape extends z.ZodType>(type: Type, shape: Shape) =>
  z
    .object({
      key: outputKey,
      label: text(200),
      type: z.literal(type),
      shape: shape.optional(),
      /** Absent reads as `required`; only an output with a shape has one. */
      purpose: z.enum(OUTPUT_PURPOSES).optional(),
    })
    .strict()
    .refine((output) => output.purpose === undefined || output.shape !== undefined);

/** An output a step leaves behind, with the shape its type may declare. */
const outputSchema = z.discriminatedUnion("type", [
  outputOf("value", valueShapeSchema),
  outputOf("file", z.object({ fileKind: z.enum(OUTPUT_FILE_KINDS) }).strict()),
  /** `use` is the key of one of the step's `uses`: the account the record is in. */
  outputOf("record", z.object({ use: useKey, objectKind: text(200) }).strict()),
  /** `use` is the key of one of the step's `uses`: the account that sends it. */
  outputOf("message", z.object({ use: useKey, recipient: recipientSchema }).strict()),
  /** Given by someone the step asks. */
  outputOf("attestation", z.object({ statement: text(500) }).strict()),
]);
export type Output = z.infer<typeof outputSchema>;
export type OutputShape = NonNullable<Output["shape"]>;

/** How a step reads an earlier output: its type, and for a value optionally its kind. */
export const outputAsSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("value"), kind: z.enum(OUTPUT_VALUE_KINDS).optional() }).strict(),
  z.object({ type: z.literal("file") }).strict(),
  z.object({ type: z.literal("record") }).strict(),
  z.object({ type: z.literal("message") }).strict(),
  z.object({ type: z.literal("attestation") }).strict(),
]);
export type OutputAs = z.infer<typeof outputAsSchema>;

/** An earlier output a step reads. */
const needSchema = z
  .object({ stepId: idSchema("processStep"), key: outputKey, as: outputAsSchema })
  .strict();
export type Need = z.infer<typeof needSchema>;

/** Whether an output is required, the default, rather than evidence. */
export const isRequired = (output: Output): boolean => output.purpose !== "evidence";

/**
 * Whether an output is a Done when on its own (Q12): a shaped, required record, message or
 * attestation is confirmed by something outside Abacor (read back, sent, given by a member). A
 * value or a file Abacor can make alone, evidence never counts, and a legacy output has no check.
 */
export const confirmsDone = (output: Output): boolean =>
  output.shape !== undefined &&
  isRequired(output) &&
  (output.type === "record" || output.type === "message" || output.type === "attestation");

/** What confirms an output when a case runs: the rule the case module's `isConfirmed` enforces. */
export type DoneCheck = "present" | "read_back" | "sent" | "attested";

export function doneCheckOf(output: Pick<Output, "type">): DoneCheck {
  switch (output.type) {
    case "value":
    case "file":
      return "present";
    case "record":
      return "read_back";
    case "message":
      return "sent";
    case "attestation":
      return "attested";
  }
}

/**
 * Whether a step that reads `output` as `as` gets what it expects: the same type and, for a value
 * read as a kind, that kind. A legacy value has no kind, so it reads as any.
 */
export function readsAs(output: Output, as: OutputAs): boolean {
  if (output.type !== as.type) return false;
  if (output.type !== "value" || as.type !== "value" || as.kind === undefined) return true;
  return output.shape === undefined || output.shape.kind === as.kind;
}

export const stepBodySchema = z
  .object({
    /** The step's sentence: the outcome, in the firm's words. */
    title: decided(text(500)),
    effectKind: decided(z.enum(EFFECT_KINDS)),
    /** Only the answers that differ from the defaults. */
    answers: z
      .object(
        Object.fromEntries(ANSWER_FIELDS.map((field) => [field, answer.optional()])) as {
          [Field in AnswerField]: z.ZodOptional<typeof answer>;
        },
      )
      .strict(),
    outputs: z
      .array(outputSchema)
      .max(50)
      .refine((outputs) => new Set(outputs.map((output) => output.key)).size === outputs.length),
    after: z.array(afterSchema).max(50),
    /**
     * The tables and columns the step reads, beside its sentence: structure the system writes,
     * like outputs. Absent on a step that reads none, so earlier hashes stand.
     */
    reads: z.array(readSchema).max(20).optional(),
    /**
     * The exact values the step's texts name by `{{key}}`, each decided on its own. Absent on a
     * step with none, so earlier hashes stand.
     */
    slots: z
      .record(z.string().regex(SLOT_KEY), slotSchema)
      .refine((slots) => Object.keys(slots).length <= 50)
      .optional(),
    /** The step's own people; absent asks the playbook's person. */
    who: whoSchema.optional(),
    /** Whether a person confirms or does the step, set by the system; absent is derived. */
    level: decided(z.enum(STEP_LEVELS)).optional(),
    /**
     * What the step uses: apps, sites and login columns, each with what it does there and, for an
     * app or a site, its account. Replaces the free-text `answers.uses`. Absent on a step with
     * none, so earlier hashes stand.
     */
    uses: z
      .array(useSchema)
      .max(10)
      .refine((uses) => new Set(uses.map((use) => use.key)).size === uses.length)
      .optional(),
    /**
     * The earlier outputs the step reads, one per step and key, each with the type it reads it
     * as. The service derives an `after` edge from each. Absent on a step that reads none, so
     * earlier hashes stand.
     */
    needs: z
      .array(needSchema)
      .max(50)
      .refine(
        (needs) => new Set(needs.map((need) => `${need.stepId}/${need.key}`)).size === needs.length,
      )
      .optional(),
    /**
     * How the step is done (SV23): the know-how a person was told or Abacor learned, in the order
     * it was said. Done when stays one checkable sentence; this holds the fields, clicks and
     * lookups. Absent on a step with none, so earlier hashes stand.
     */
    how: z.array(howNoteSchema).min(1).max(HOW_NOTES_MAX).optional(),
  })
  .strict();
export type StepBody = z.infer<typeof stepBodySchema>;

/**
 * The fields of a step a `set_field` change can name. Slots and Who have their own ops
 * (`set_slot`, `set_who`), so a person's value edit never rewrites the structure around it.
 */
export const STEP_FIELDS = [
  "title",
  "effectKind",
  "outputs",
  "after",
  "reads",
  "level",
  "uses",
  "needs",
  "how",
  ...ANSWER_FIELDS.map((field) => `answers.${field}` as const),
] as const;
export type StepField = (typeof STEP_FIELDS)[number];

/** A step's fields that are optional in the body: absent when unset, so earlier hashes stand. */
export const OPTIONAL_STEP_FIELDS = ["reads", "level", "uses", "needs", "how"] as const;

/**
 * The schema of one step field's value, so a field's value can be checked without the rest of the
 * step: an answer may also be null, which leaves it to the default.
 */
export function stepFieldSchema(field: StepField) {
  if (field.startsWith("answers.")) return answer.nullable();
  return stepBodySchema.shape[field as Exclude<StepField, `answers.${string}`>];
}

/** What a change names when it writes one slot of a step. */
export type SlotField = `slots.${string}`;

/**
 * Who does the work on a step. A level the system set wins; without one, a step that changes or
 * sends is Abacor's with a person confirming and the rest are Abacor's. A step written before
 * levels with Who as text stands for a person, as it did. Naming who confirms (`who`) does not make
 * a step a person's.
 */
export function levelOf(body: StepBody): Level {
  if (body.level) return body.level.value;
  if (body.answers.who) return "human";
  const kind = body.effectKind.value;
  return kind === "changes" || kind === "sends" ? "confirms" : "abacor";
}

const LEVEL_ORDER: Level[] = ["human", "confirms", "abacor"];
const DECISION_LEVEL: Record<Decision, Level> = {
  always: "abacor",
  ask_first: "confirms",
  never: "human",
};

/**
 * The level a step shows: the lower of its own level and what each use's account allows for what
 * the use does, so the card never claims more than the account allows. `rules` holds, by use key,
 * the account's rule for that use's `does`; a use with no rule there leaves the level as it is. A
 * step whose account asks first reads as a person confirming.
 */
export function shownLevel(body: StepBody, rules: Record<string, Decision>): Level {
  let level = levelOf(body);
  for (const use of body.uses ?? []) {
    const rule = Object.hasOwn(rules, use.key) ? rules[use.key] : undefined;
    if (!rule) continue;
    const allowed = DECISION_LEVEL[rule];
    if (LEVEL_ORDER.indexOf(allowed) < LEVEL_ORDER.indexOf(level)) level = allowed;
  }
  return level;
}

/** A step's text as plain runs and the slot keys it names, in order. */
export function parseSlotText(text: string): (string | { key: string })[] {
  const parts: (string | { key: string })[] = [];
  let at = 0;
  for (const match of text.matchAll(SLOT_TOKEN)) {
    if (match.index > at) parts.push(text.slice(at, match.index));
    parts.push({ key: match[1] ?? "" });
    at = match.index + match[0].length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

const UNIT_WORDS: Record<DurationUnit, [string, string]> = {
  minutes: ["minute", "minutes"],
  hours: ["hour", "hours"],
  businessDays: ["business day", "business days"],
  days: ["day", "days"],
  weeks: ["week", "weeks"],
};

/** The digits after the point a currency's minor units stand for (2 for AUD, 0 for JPY). */
function minorDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** A slot's value in plain words: "2 business days", "AUD 10,000.00", "09:30", a member's name. */
export function slotText(slot: Slot, nameOf?: (userId: string) => string | null): string {
  switch (slot.type) {
    case "duration": {
      const [one, many] = UNIT_WORDS[slot.value.unit];
      return `${slot.value.amount.toLocaleString("en-US")} ${slot.value.amount === 1 ? one : many}`;
    }
    case "count":
      return slot.value.toLocaleString("en-US");
    case "amount": {
      const digits = minorDigits(slot.value.currency);
      const major = (slot.value.minor / 10 ** digits).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      return `${slot.value.currency} ${major}`;
    }
    case "timeOfDay":
      return slot.value.time;
    case "member":
      if (!nameOf) return "a member";
      return nameOf(slot.value.userId) ?? "a former member";
  }
}

/**
 * A step's text for a reader who must never see a token: the chat's summary, a case's prompt, an
 * export. Each `{{key}}` naming a slot of the step becomes its value in plain words; braces naming
 * no slot (text written before slots) are left as they were written. `nameOf` gives a member's
 * name, or null for someone no longer in the workspace.
 */
export function renderStepText(
  body: StepBody,
  text: string,
  nameOf?: (userId: string) => string | null,
): string {
  return parseSlotText(text)
    .map((part) => {
      if (typeof part === "string") return part;
      const slot = body.slots?.[part.key];
      return slot ? slotText(slot, nameOf) : `{{${part.key}}}`;
    })
    .join("");
}

const outputRefSchema = z.object({ stepId: idSchema("processStep"), key: outputKey }).strict();

/** One SOP passage marked as not work, by its file and ¶ number, with why. */
const notWorkSchema = z
  .object({ file: idSchema("file"), passage: z.int().positive().max(10_000), reason: text(300) })
  .strict();

/**
 * One field of what Starts when hands a case (decision `2026-11-03-tries-on-the-case-runner-build-plan`,
 * item 3): declared the way a step declares an output, as a value of one kind or a file of one
 * kind, always per case. A case opens only with a value that fits each field it requires.
 */
const startFieldOf = <Type extends "value" | "file", Shape extends z.ZodType>(
  type: Type,
  shape: Shape,
) =>
  z
    .object({
      key: outputKey,
      label: text(200),
      type: z.literal(type),
      shape,
      /** Absent reads as required: a case can't open without it. */
      optional: z.literal(true).optional(),
    })
    .strict();

export const startFieldSchema = z.discriminatedUnion("type", [
  startFieldOf(
    "value",
    valueShapeSchema.refine((shape) => shape.per === "case"),
  ),
  startFieldOf("file", z.object({ fileKind: z.enum(OUTPUT_FILE_KINDS) }).strict()),
]);
export type StartField = z.infer<typeof startFieldSchema>;

/** The most fields Starts when hands a case. */
export const START_FIELDS_MAX = 30;

/**
 * The process facts a case runs on, versioned with the steps: what one case is (amendment A6) and
 * which outputs make a case complete (A7). The process's name, owner and approver are not here:
 * they follow the process's current value, not a release.
 */
export const processFactsSchema = z
  .object({
    caseUnit: decided(text(200)).nullable(),
    delivers: decided(
      z
        .array(outputRefSchema)
        .max(50)
        .refine(
          (refs) => new Set(refs.map((ref) => `${ref.stepId}/${ref.key}`)).size === refs.length,
        ),
    ).nullable(),
    /**
     * The event that starts a case, in the SOP's words (concept map item 5): words only, never set
     * up or turned on, until triggers exist (ENG-1052). Absent when unset, so earlier facts hash as
     * they did.
     */
    startsWhen: decided(text(500)).optional(),
    /**
     * What Starts when hands a case, as typed fields (decision
     * `2026-11-03-tries-on-the-case-runner-build-plan`, item 3): every case, live or a try, opens
     * from values checked against them. Absent when unset, so earlier facts hash as they did.
     */
    startsWith: decided(
      z
        .array(startFieldSchema)
        .max(START_FIELDS_MAX)
        .refine((fields) => new Set(fields.map((field) => field.key)).size === fields.length),
    ).optional(),
    /**
     * The SOP passages that are not work (background, purpose), each with its reason (concept map
     * item 14): with the passages a value cites, they account for every passage. Absent when unset.
     */
    notWork: decided(
      z
        .array(notWorkSchema)
        .max(500)
        .refine(
          (marks) =>
            new Set(marks.map((mark) => `${mark.file}/${String(mark.passage)}`)).size ===
            marks.length,
        ),
    ).optional(),
  })
  .strict();
export type ProcessFacts = z.infer<typeof processFactsSchema>;

/** The process facts a change can name. */
export const FACT_FIELDS = ["caseUnit", "delivers", "startsWhen", "startsWith", "notWork"] as const;
export type FactField = (typeof FACT_FIELDS)[number];

/** The facts that are absent rather than null when unset, so facts written before them hash the same. */
export const OPTIONAL_FACT_FIELDS = [
  "startsWhen",
  "startsWith",
  "notWork",
] as const satisfies readonly FactField[];

/**
 * `facts` with `field` set to `value`, or cleared: an optional fact is taken out rather than set to
 * null, so clearing it gives back the hash it had before it was written.
 */
export function withFact(facts: ProcessFacts, field: FactField, value: unknown): unknown {
  if (value !== null && value !== undefined) return { ...facts, [field]: value };
  if (!(OPTIONAL_FACT_FIELDS as readonly string[]).includes(field))
    return { ...facts, [field]: null };
  const { [field]: _cleared, ...rest } = facts;
  return rest;
}

/**
 * What a change records as its before and after. Which one depends on the change: a step added or
 * removed records the step; a move records order keys; a field or fact records that value, or null
 * where an answer or a fact was not written.
 */
/** A step's place and body: what a merge compares, and a conflict's base, ours and theirs hold. */
export type StepVersion = { orderKey: string; body: StepBody };

export type ChangeValue =
  | StepBody
  /** A step as a merge took it from live; null where it was absent. */
  | StepVersion
  /** A step's sentence, one of its answers, or the case unit. */
  | Decided<string>
  | StepBody["effectKind"]
  | StepBody["outputs"]
  | StepBody["after"]
  | NonNullable<StepBody["reads"]>
  /** One slot of a step; null where the step had none by that key. */
  | Slot
  /** A step's own people; null where it asked the playbook's person. */
  | Who
  /** What a step uses, or one use's account (`uses.<key>`). */
  | NonNullable<StepBody["uses"]>
  /** The earlier outputs a step reads. */
  | NonNullable<StepBody["needs"]>
  /** How a step is done: its notes. */
  | NonNullable<StepBody["how"]>
  | Decided<string | null>
  | NonNullable<ProcessFacts["delivers"]>
  /** The SOP passages marked as not work. */
  | NonNullable<ProcessFacts["notWork"]>
  /** What Starts when hands a case. */
  | NonNullable<ProcessFacts["startsWith"]>
  /** A pin, by its summary. */
  | PinSummary
  /** An order key. */
  | string
  | null;

export const NO_FACTS: ProcessFacts = { caseUnit: null, delivers: null };

/**
 * The key a case looked a row up by (ENG-1075): each key column's value as the case had it, by
 * the column's ID, in words. One to five columns.
 */
export const tableKeySchema = z.record(idSchema("dataColumn"), text(500)).refine((key) => {
  const size = Object.keys(key).length;
  return size >= 1 && size <= 5;
});
export type TableKey = z.infer<typeof tableKeySchema>;

export const questionTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("process"), id: idSchema("process") }).strict(),
  z.object({ kind: z.literal("step"), id: idSchema("processStep") }).strict(),
  z.object({ kind: z.literal("table_row"), id: idSchema("dataTableRow") }).strict(),
  /** A row the table does not have: the table, and the key a case looked for in it. */
  z
    .object({ kind: z.literal("table_key"), id: idSchema("dataTable"), key: tableKeySchema })
    .strict(),
]);
export type QuestionTarget = z.infer<typeof questionTargetSchema>;

/**
 * How a person says their answer reaches cases. A table edit names the row it edited, which must
 * be in the table named; a definition change names the open draft that will carry it.
 */
export const questionResolutionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }).strict(),
  z
    .object({
      kind: z.literal("table"),
      tableId: idSchema("dataTable"),
      rowId: idSchema("dataTableRow"),
    })
    .strict(),
  z.object({ kind: z.literal("release"), draftId: idSchema("processDraft") }).strict(),
]);
export type QuestionResolution = z.infer<typeof questionResolutionSchema>;

const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);

/**
 * The document a release freezes: everything it says, inside its hash, so an exported release can
 * be read and checked with no live service. Never changed once written. Fields that later slices
 * add (pinned table rows, the capability manifest) are new and optional; a release without them
 * has none.
 */
export const releaseContentSchema = z
  .object({
    format: z.literal(1),
    name: text(200),
    facts: processFactsSchema,
    /** Each part carries its own hash, so a draft cut from this release knows its base. */
    factsHash: sha256Hex,
    steps: z.array(
      z
        .object({
          stepId: idSchema("processStep"),
          orderKey: z.string().refine((value) => isOrderKey(value)),
          body: stepBodySchema,
          hash: sha256Hex,
        })
        .strict(),
    ),
    /**
     * The tables its change and send steps read, pinned (decision 117). Absent on a release that
     * pins none, so earlier releases read the same.
     */
    tables: z.array(pinnedTableSchema).optional(),
    /** The questions open when it was released; holds are read from the live rows (decision 107). */
    openQuestions: z.array(
      z
        .object({
          id: idSchema("processQuestion"),
          kind: z.enum(QUESTION_KINDS),
          target: questionTargetSchema,
          text: text(2000),
        })
        .strict(),
    ),
  })
  .strict();
export type ReleaseContent = z.infer<typeof releaseContentSchema>;

// Order keys. Steps sort by a key that is a string of base-36 digits, so a step goes in between two
// others without renumbering anything around it. A key never ends in "0", which keeps room below
// every key; the digits and that rule are all `isOrderKey` checks.

const DIGITS = "0123456789abcdefghijklmnopqrstuvwxyz";
const BASE = DIGITS.length;
const ORDER_KEY = /^[0-9a-z]*[1-9a-z]$/;

/** Any length: a key grows only when steps keep going in at the same place, and slowly. */
export const isOrderKey = (value: unknown): value is string =>
  typeof value === "string" && ORDER_KEY.test(value);

const digitAt = (key: string, index: number) => DIGITS.indexOf(key.charAt(index));

/** A key after `low` (or the start) and before `high` (or the end), as short as it can be. */
function midpoint(low: string, high: string | null): string {
  let prefix = "";
  for (let index = 0; ; index++) {
    const below = index < low.length ? digitAt(low, index) : 0;
    const above = high !== null && index < high.length ? digitAt(high, index) : BASE;
    if (below === above) {
      prefix += DIGITS.charAt(below);
      continue;
    }
    if (above - below > 1) return prefix + DIGITS.charAt(Math.floor((below + above) / 2));
    // Neighboring digits: keep the lower one, then anything after `low`'s remaining digits fits.
    return prefix + DIGITS.charAt(below) + midpoint(low.slice(index + 1), null);
  }
}

/** A key strictly between `before` and `after`; null means the start or the end. */
export function keyBetween(before: string | null, after: string | null): string {
  if (before !== null && !isOrderKey(before)) throw new RangeError("Not an order key.");
  if (after !== null && !isOrderKey(after)) throw new RangeError("Not an order key.");
  if (before !== null && after !== null && before >= after) {
    throw new RangeError("The keys are not in order.");
  }
  // Adding a last or a first step is the common case, so it steps one digit up or down rather
  // than halving the gap, which would grow the key by a character every few steps.
  if (before !== null && after === null) {
    const index = before.search(/[^z]/);
    if (index === -1) return `${before}1`;
    return before.slice(0, index) + DIGITS.charAt(digitAt(before, index) + 1);
  }
  if (before === null && after !== null) {
    // Down one from the first digit above "1": stepping a "1" down would end the key in "0".
    const index = after.search(/[2-9a-z]/);
    if (index !== -1) return after.slice(0, index) + DIGITS.charAt(digitAt(after, index) - 1);
    // Only 0s and 1s, ending in "1": the last "1" becomes "0z", the top of the room below it.
    return `${after.slice(0, -1)}0z`;
  }
  return midpoint(before ?? "", after);
}

/**
 * An SOP's passages, ¶1 to ¶N in order (ENG-1073 item 2, concept map item 14): each paragraph,
 * each list item and each table body row. Headings, a table's header row and its rule line, and
 * HTML comments are not passages. A paragraph's lines join with one space; an indented line right
 * after a list item continues it. Line endings do not matter, so the same text always splits the
 * same way, and a source cites a passage by its 1-based number.
 */
export function sopPassages(text: string): string[] {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .replace(/<!--[\s\S]*?-->/g, "")
    .split("\n");
  const found: string[] = [];
  let open: string[] = [];
  /** Whether the open passage is a list item, which an indented line continues. */
  let listItem = false;
  const flush = () => {
    if (open.length > 0) found.push(open.join(" "));
    open = [];
    listItem = false;
  };
  // A table's separator row, with or without its outer pipes ("--- | ---" or "|---|---|").
  const isRule = (line: string) =>
    line.includes("|") && /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/.test(line);
  /** Whether the lines since the last separator row are a table's rows. */
  let inTable = false;
  for (const [index, raw] of lines.entries()) {
    const line = raw.trim();
    const next = lines[index + 1]?.trim() ?? "";
    if (line === "" || line.startsWith("#")) {
      flush();
      inTable = false;
    } else if (
      line.startsWith("|") ||
      (line.includes("|") && (inTable || isRule(line) || isRule(next)))
    ) {
      // A table: each body row is its own passage; the header and separator are not.
      flush();
      if (isRule(line)) inTable = true;
      else if (!isRule(next)) found.push(line);
    } else if (/^(\d+[.)]|[-*+])\s/.test(line)) {
      flush();
      inTable = false;
      open.push(line);
      listItem = true;
    } else if (listItem && /^\s/.test(raw)) {
      open.push(line);
    } else {
      if (listItem) flush();
      inTable = false;
      open.push(line);
    }
  }
  flush();
  return found;
}
